/**
 * Hooks for the Magic checker + Adspect integration toggles on the
 * Advanced sub-tab. Each is a per-campaign GET + PUT pair.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';
import { binomKeys } from './useBinom';

export interface BinomMagicCheckerSettings {
  isEnabled: boolean;
  magicCheckerId: number | null;
  binomMagicCheckerId: number | null;
  name: string | null;
}

export interface BinomAdspectSettings {
  enabled: boolean;
  enabled_tags: string[];
  list: unknown[];
  used_fingerprint: boolean;
  white_list_enabled: boolean;
}

const magicKey = (id: string) =>
  [...binomKeys.campaign(id), 'magic-checker'] as const;
const adspectKey = (id: string) =>
  [...binomKeys.campaign(id), 'adspect'] as const;

export function useBinomMagicChecker(id: string | undefined) {
  return useQuery({
    queryKey: magicKey(id ?? ''),
    queryFn: async () => {
      const r = await api.get<{ data: BinomMagicCheckerSettings | null }>(
        `/binom/magic-checker/campaign/${encodeURIComponent(id!)}`,
      );
      return r.data?.data ?? null;
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useUpdateBinomMagicChecker(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BinomMagicCheckerSettings) => {
      await api.put(
        `/binom/magic-checker/campaign/${encodeURIComponent(id!)}`,
        body,
      );
    },
    onSuccess: () => {
      if (id) qc.invalidateQueries({ queryKey: magicKey(id) });
    },
  });
}

export function useBinomAdspect(id: string | undefined) {
  return useQuery({
    queryKey: adspectKey(id ?? ''),
    queryFn: async () => {
      const r = await api.get<{ data: BinomAdspectSettings | null }>(
        `/binom/adspect/campaign/${encodeURIComponent(id!)}`,
      );
      return r.data?.data ?? null;
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useUpdateBinomAdspect(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BinomAdspectSettings) => {
      await api.put(
        `/binom/adspect/campaign/${encodeURIComponent(id!)}`,
        body,
      );
    },
    onSuccess: () => {
      if (id) qc.invalidateQueries({ queryKey: adspectKey(id) });
    },
  });
}
